import React, { useState, useRef, useEffect } from "react";
import { getMagic } from "../lib/magic";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const STEPS = { FORM: "form", OTP: "otp", LOADING: "loading", DONE: "done" };

// Redes soportadas por la wallet creada en Walletven
const SUPPORTED_NETWORKS = [
  "Arbitrum", "Base", "Berachain", "Binance Smart Chain", "Celo", "Chiliz",
  "Cronos", "Etherlink", "Fantom", "Flare", "Harmony", "Hedera", "Horizen EON",
  "Loopring", "Moonbeam", "Optimism", "Plume", "Polygon", "Rari Chain", "Sei",
  "Soneium", "Stability", "XDC Network", "ZetaChain", "zkSync",
];

export default function CreateWalletModal({ open, onClose, onWalletCreated }) {
  const { user, refreshUser, login } = useAuth();

  // Guest fields
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");

  const [step, setStep]         = useState(STEPS.FORM);
  const [otp, setOtp]           = useState("");
  const [address, setAddress]   = useState("");
  const [copied, setCopied]     = useState(false);
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [showNetworks, setShowNetworks] = useState(false); // Advertencia popup
  const handleRef               = useRef(null);
  const otpSentRef              = useRef(false);

  // Logged-in: auto-send OTP on open (skip the form entirely)
  useEffect(() => {
    if (open && user?.email) {
      setEmail(user.email);
      const t = setTimeout(() => sendOtpToEmail(user.email), 300);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const handleClose = () => {
    setStep(STEPS.FORM); setName(""); setEmail(""); setPassword(""); setOtp("");
    setAddress(""); setCopied(false); setError(""); setBusy(false);
    setShowNetworks(false);
    handleRef.current = null; otpSentRef.current = false;
    onClose();
  };

  const goToOtp = () => {
    if (!otpSentRef.current) {
      otpSentRef.current = true;
      setBusy(false);
      setStep(STEPS.OTP);
    }
  };

  // ── Send Magic OTP ────────────────────────────────────────────────────────
  const sendOtpToEmail = async (targetEmail) => {
    setError(""); setBusy(true); otpSentRef.current = false;
    setStep(STEPS.LOADING);
    try {
      const magic  = getMagic();
      const handle = magic.auth.loginWithEmailOTP({ email: targetEmail, showUI: false });
      handleRef.current = handle;
      handle.on("email-otp-sent", goToOtp);
      setTimeout(goToOtp, 5000);
      handle.catch((err) => {
        console.error("Magic handle error:", err);
        setError("Código expirado o cancelado. Intenta de nuevo.");
        setBusy(false);
        setStep(user?.email ? STEPS.OTP : STEPS.FORM);
        otpSentRef.current = false;
      });
    } catch (err) {
      console.error(err);
      setError("No se pudo enviar el código. Intenta de nuevo.");
      setBusy(false);
      setStep(user?.email ? STEPS.OTP : STEPS.FORM);
    }
  };

  // Guest form submit — validate, check for duplicate, then send OTP
  const submitForm = async () => {
    if (!name.trim())              return setError("Ingresa tu nombre.");
    if (!email.includes("@"))      return setError("Ingresa un correo válido.");
    if (password.length < 6)       return setError("La contraseña debe tener al menos 6 caracteres.");
    setError(""); setBusy(true);
    try {
      const { data } = await client.get(`/api/auth/check-email?email=${encodeURIComponent(email)}`);
      if (data.exists) {
        setBusy(false);
        setError("Este correo ya tiene una cuenta. Inicia sesión para vincular tu wallet.");
        return;
      }
    } catch (e) {
      // if the check fails, proceed anyway — register will catch the duplicate
    }
    await sendOtpToEmail(email);
  };

  // ── Verify OTP → create wallet → (if guest) register in DB ───────────────
  const verifyOtp = async () => {
    if (!otp || otp.length < 6) return setError("Ingresa el código de 6 dígitos.");
    setError(""); setBusy(true); setStep(STEPS.LOADING);
    try {
      handleRef.current.emit("verify-email-otp", otp);
      await handleRef.current;

      // Get wallet address
      const magic = getMagic();
      let walletAddress = null;
      try {
        const info = await magic.user.getInfo();
        walletAddress = info?.publicAddress;
      } catch (e) {}
      if (!walletAddress) {
        try {
          const accounts = await magic.rpcProvider.request({ method: "eth_accounts", params: [] });
          walletAddress = accounts?.[0];
        } catch (e) {}
      }
      if (!walletAddress) throw new Error("No se pudo obtener la dirección de wallet.");

      if (user) {
        // ── Logged-in: just save the address ─────────────────────────────
        await client.patch("/api/auth/wallet", { walletAddress });
        await refreshUser();
      } else {
        // ── Guest: register a full Walletven account with the wallet ──────
        const nameParts = name.trim().split(" ");
        const firstName = nameParts[0];
        const lastName  = nameParts.slice(1).join(" ") || "—";

        try {
          // Register account
          const { data } = await client.post("/api/auth/register", {
            name: firstName,
            lastName,
            email,
            password,
          });
          // Save JWT so subsequent calls are authenticated
          localStorage.setItem("cp_token", data.token);
          // Now save the wallet address
          await client.patch("/api/auth/wallet", { walletAddress });
          // Refresh auth context so Navbar/profile update immediately
          await refreshUser();
        } catch (regErr) {
          const msg = regErr.response?.data?.message;
          if (msg === "email_in_use") {
            // Account already exists — just log them in and attach the wallet
            await login(email, password);
            await client.patch("/api/auth/wallet", { walletAddress });
            await refreshUser();
          } else {
            throw new Error("No se pudo crear la cuenta. Intenta de nuevo.");
          }
        }
      }

      setAddress(walletAddress);
      setStep(STEPS.DONE);
      if (onWalletCreated) onWalletCreated(walletAddress);
    } catch (err) {
      console.error(err);
      setError(
        ["No se pudo obtener la dirección de wallet.", "No se pudo crear la cuenta. Intenta de nuevo."].includes(err.message)
          ? err.message
          : "Código incorrecto o expirado. Solicita uno nuevo."
      );
      setBusy(false);
      setStep(STEPS.OTP);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="modal-backdrop">
        <div className="modal-card wallet-modal">
          <div className="wallet-modal-header">
            <h3 className="modal-title">Crear mi wallet</h3>
            <button className="wallet-modal-close" onClick={handleClose}>✕</button>
          </div>

          {/* FORM — guests only; logged-in users jump straight to LOADING */}
          {step === STEPS.FORM && (
            <>
              <p className="modal-body">
                Crea tu cuenta Walletven y tu wallet de criptomonedas en un solo paso —
                sin frases semilla, sin extensiones.
              </p>

              {/* Advertencia — abre popup con redes compatibles */}
              <button
                type="button"
                className="wallet-warning-banner"
                onClick={() => setShowNetworks(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  textAlign: "left",
                  background: "rgba(245, 158, 11, 0.10)",
                  border: "1px solid rgba(245, 158, 11, 0.40)",
                  color: "var(--warning, #b45309)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  margin: "0 0 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
                <span>Advertencia: revisa las redes compatibles con tu wallet</span>
                <span style={{ marginLeft: "auto", opacity: 0.7, whiteSpace: "nowrap" }}>Ver ›</span>
              </button>

              <div className="field">
                <label>Nombre completo</label>
                <input
                  placeholder="Juan García"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="field">
                <label>Correo</label>
                <input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Contraseña</label>
                <input
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitForm()}
                />
              </div>
              {error && (
                error.includes("Inicia sesión") ? (
                  <p className="error-text">
                    Este correo ya tiene una cuenta.{" "}
                    <a href="/login" style={{ color: "var(--danger)", fontWeight: 600 }}>
                      Inicia sesión
                    </a>{" "}
                    para vincular tu wallet.
                  </p>
                ) : (
                  <p className="error-text">{error}</p>
                )
              )}
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={handleClose}>Cancelar</button>
                <button className="btn btn-primary" onClick={submitForm} disabled={busy}>
                  {busy ? "Enviando..." : "Continuar"}
                </button>
              </div>
              <p className="fine-print">
                Recibirás un código de verificación en tu correo para confirmar tu identidad.
              </p>
            </>
          )}

          {/* OTP */}
          {step === STEPS.OTP && (
            <>
              <div className="wallet-modal-status">
                <div className="wallet-modal-waiting-icon">✉️</div>
                <p className="status-title">Revisa tu correo</p>
                <p className="status-text">
                  Enviamos un código de 6 dígitos a <strong>{email}</strong>.
                </p>
              </div>
              <div className="field">
                <label>Código de verificación</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
                  autoFocus
                  style={{ fontFamily: "var(--font-mono)", fontSize: 26, letterSpacing: 10, textAlign: "center" }}
                />
              </div>
              {error && <p className="error-text">{error}</p>}
              <div className="modal-actions">
                <button className="btn btn-ghost"
                  onClick={() => { setStep(STEPS.FORM); setOtp(""); setError(""); }}>
                  Volver
                </button>
                <button className="btn btn-primary" onClick={verifyOtp} disabled={busy}>
                  {busy ? "Verificando..." : "Confirmar"}
                </button>
              </div>
            </>
          )}

          {/* LOADING */}
          {step === STEPS.LOADING && (
            <div className="wallet-modal-status">
              <span className="spinner" />
              <p className="status-text" style={{ marginTop: 16 }}>
                {otp ? "Creando tu cuenta y wallet..." : `Enviando código a ${email}...`}
              </p>
            </div>
          )}

          {/* DONE */}
          {step === STEPS.DONE && (
            <div className="wallet-modal-status">
              <div className="wallet-modal-success-icon">✓</div>
              <p className="status-title" style={{ color: "var(--primary)" }}>
                {user ? "¡Wallet creada!" : "¡Cuenta y wallet creadas!"}
              </p>
              <p className="status-text">
                Esta es tu dirección. Cópiala y pégala cuando el proveedor de pagos te la solicite.
              </p>
              <div className="wallet-address-box">
                <span className="wallet-address-text">{address}</span>
                <button className="btn btn-primary wallet-copy-btn" onClick={copyAddress}>
                  {copied ? "¡Copiado!" : "Copiar"}
                </button>
              </div>
              <div className="wallet-networks">
                <p className="wallet-networks-label">✅ Redes compatibles con tu wallet:</p>
                <div className="wallet-networks-list">
                  {SUPPORTED_NETWORKS.map((n) => (
                    <span key={n} className="wallet-network-tag">{n}</span>
                  ))}
                </div>
                <p className="wallet-networks-warn">
                  ⚠️ <strong>Importante:</strong> no envíes fondos desde redes que no estén en esta lista.
                  Si tienes dudas, consulta primero con Atención al Cliente.
                </p>
              </div>
              <p className="fine-print">
                Vinculada a <strong>{email}</strong>. Puedes recuperarla repitiendo el
                mismo proceso con el mismo correo en cualquier momento.
              </p>
              <button className="btn btn-ghost btn-block" onClick={handleClose} style={{ marginTop: 12 }}>
                Listo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Advertencia popup: redes compatibles ─────────────────────────── */}
      {showNetworks && (
        <div
          onClick={() => setShowNetworks(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1100, padding: 16,
          }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480, width: "100%" }}
          >
            <div className="wallet-modal-header">
              <h3 className="modal-title">⚠️ Advertencia</h3>
              <button className="wallet-modal-close" onClick={() => setShowNetworks(false)}>✕</button>
            </div>
            <p className="modal-body">
              La wallet creada en Walletven <strong>solo soporta</strong> las siguientes redes:
            </p>
            <div className="wallet-networks-list" style={{display: "flex", flexWrap: "wrap", gap: "10px"}}>
              {SUPPORTED_NETWORKS.map((n) => (
                <span key={n} className="wallet-network-tag">{n}</span>
              ))}
            </div>
            <p className="fine-print" style={{ marginTop: 16 }}>
              Si tienes dudas, consulta primero con <strong>Atención al Cliente</strong>.
            </p>
            <button
              className="btn btn-primary btn-block"
              onClick={() => setShowNetworks(false)}
              style={{ marginTop: 16 }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}